<script setup lang="ts">
import {ButtonType} from "@Common/Composables/button-styles";
import Slider from "@vueform/slider";
import CheckboxInput from "@Components/Input/CheckboxInput.vue";
import Button from "@Components/Buttons/Button.vue";
import {onBeforeMount, ref} from "vue";
import MoneyInput from "@Components/Input/MoneyInput.vue";
import Notice from '@Common/Components/Notice/Notice.vue';
import useCurrentLocale from "@Common/Composables/current-locale";
import trackingEvents from '@Helpers/trackingEvents.json';
import {trackData} from '@Helpers/trackData';

const props = defineProps({
    schedule: {
        type: Boolean,
        required: true
    },
    min: {
        type: Number,
        required: true
    },
    max: {
        type: Number,
        required: true
    },
    tip: {
        type: Number,
        required: true
    },
    total: {
        type: Number,
        required: true
    },
    minDonation: {
        type: Number,
        default: 1
    }
});

const {i18n, formatCurrency} = useCurrentLocale();

const {t} = i18n;

const emit = defineEmits(['close']);

const tipAmount = ref(props.tip);

const enableTip = ref(props.tip > 0);

const applyTip = () => {
    emit('close', {
        tip: tipAmount.value,
        totalWithTip: parseFloat((props.total + tipAmount.value).toFixed(2))
    });
};

const manualChangeAmount = (value: string) => {
    let tip = parseFloat(value);
    if (isNaN(tip)) {
        tip = tipAmount.value;
    }
    if (tip <= 0) {
        enableTip.value = false;
        tip = 0;
    } else {
        enableTip.value = true;
    }
    tipAmount.value = tip;
};

const changeAmount = (tipValue: number) => {
    tipAmount.value = tipValue;
    enableTip.value = true;
};

const toggleTip = (newValue: boolean) => {
    enableTip.value = !newValue;
    tipAmount.value = (enableTip.value) ? props.tip : 0;
};

onBeforeMount(() => {
    trackData({
        event: trackingEvents.BILLING_TIP_MODIFY_CLICKED
    });
});
</script>
<template>
    <div class="modal-payment-tip">
        <div class="modal-tip-content">
            <div class="modal-tip-information">
                <h3>{{ t('cms.modules.payment_tip.title') }}</h3>
                <p>{{ t('cms.modules.payment_tip.description') }}</p>
            </div>
            <div class="modal-tip-amount">
                <h4>{{ t('cms.modules.payment_tip.edit_title') }}</h4>
                <Slider v-if="min < max" v-model="tipAmount" :tooltips="false" :min="min" :max="max"
                        :step="max > 2 ? 0.5 : 0.05" @slide="changeAmount"/>

                <MoneyInput id="tip-amount" :name="`amount`" :value="tipAmount" @update="manualChangeAmount"/>
            </div>
        </div>

        <div :class="['actions-container', { 'disable-tip': !enableTip }]">
            <div class="tip-container">
                <CheckboxInput
                    :id="`disable-tip`"
                    :value="!enableTip"
                    @update="toggleTip"
                    :value-label="t('cms.modules.payment_tip.do_not_wish_make_contribution')"/>
                <Notice v-if="!enableTip" :expand="true" role="tipnotice">
                    <template #content>
                        {{
                            t('cms.modules.payment_tip.do_not_wish_make_contribution_notice', {donation: formatCurrency(minDonation, {minimumFractionDigits: 0})})
                        }} <a class="y-underline y-cursor-pointer y-text-blue-800 hover:y-text-blue-900"
                              @click="changeAmount(minDonation)">{{
                            t('cms.modules.payment_tip.do_not_wish_make_contribution_notice_link', {donation: formatCurrency(minDonation, {minimumFractionDigits: 0})})
                        }}</a>
                    </template>
                </Notice>
            </div>
            <div class="buttons-container">
                <Button :type="ButtonType.LINK" @click="emit('close')">{{ t('cancel') }}</Button>
                <Button :class="`apply-tip`" @click="applyTip" data-testid="apply-tip">
                    {{ t('cms.modules.payment_tip.apply') }}
                </Button>
            </div>
        </div>
    </div>
</template>

<style src="@vueform/slider/themes/default.css"></style>
<style lang="scss">
@import '@Common/Styles/variables';

.modal-payment-tip {
    .modal-tip-content {
        align-items: center;
        margin: 2rem 0;

        .modal-tip-information {
            width: 100%;

            h3, p {
                text-transform: none;
                margin-bottom: 3rem;
            }
        }

        .modal-tip-amount {
            width: 100%;

            input {
                text-align: center;
                font-weight: bold;
                font-size: 2rem;
            }

            h4 {
                text-transform: none;
                margin-bottom: 3rem;
            }

            .ui-slider {
                margin: 40px 0;
            }

            .slider-target {
                margin: 4rem 0;
                --slider-handle-width: 24px;
                --slider-handle-height: 24px;
                --slider-tooltip-font-size: 1.25rem;
                --slider-tooltip-line-height: 2rem;
                --slider-handle-ring-color: #E0EAF986;
                --slider-handle-shadow: 0.5px 0.5px 2px 1px rgba(0, 87, 255, 0.2), 0 1px 1px 0px rgba(0, 87, 255, 0.25);
                --slider-connect-bg: #{map-get($yapla-blue, 500)};
                --slider-tooltip-bg: #{map-get($yapla-blue, 200)};
                --slider-tooltip-color: #{map-get($yapla-blue, 700)};
            }

            .form-group {
                .input-group {
                    max-width: 100%;
                    width: auto !important;
                }
            }
        }
    }

    .actions-container {
        margin-top: 2rem;
        padding: 0;
        display: flex;
        gap: 20px;
        flex-direction: column;

        .form-group {
            margin-bottom: 2rem;

            div {
                display: flex;

                label {
                    margin: 5px 0 0 5px;
                    font-weight: normal;
                    cursor: pointer;
                    font-style: italic;
                    font-size: 1.2rem;
                }
            }
        }

        .buttons-container {
            display: flex;
            gap: 10px;
            align-self: flex-end;
        }
    }
}

@media screen and (min-width: 576px) {
    .modal-payment-tip {
        .modal-tip-content {
            display: flex;
            flex-direction: row;
            margin: 1rem 0;

            .modal-tip-information {
                padding-right: 1rem;
                width: 50%;
            }

            .modal-tip-amount {
                padding-left: 1rem;
                width: 50%;
            }
        }

        .actions-container {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            flex-wrap: nowrap;

            .checkbox-container {
                margin-bottom: 0;
            }

            .form-group {
                margin-bottom: 0;
            }

            .buttons-container {
                align-self: flex-end;
            }

            &.disable-tip {
                flex-direction: column;

                .tip-container {
                    display: flex;
                    align-self: flex-start;
                    flex-direction: column;
                    gap: 10px;
                }
            }
        }
    }
}
</style>